import blocklyDocsMap from './docs-map.generated.json' with { type: 'json' };
import { handbookBaseUrl, type HandbookChannel } from '../../documentation/handbook-url.ts';

export type BlocklyHelpEntry = {
  tooltip: string;
  categoryPath?: string;
};

export type ResolvedBlockHelp = {
  tooltip: string;
  helpUrl: string;
};

// blockId -> relativer Doku-Pfad, von scripts/blockly-docs/generate.mjs bei jedem CI-Lauf aus den
// echten registrierten Blöcken neu geschrieben (siehe .github/workflows/docusaurus.yml). Deckt -
// anders als helpEntries oben - ALLE Blöcke ab, nicht nur die kuratierten Standard-Blöcke.
const BLOCKLY_DOCS_MAP: Record<string, string> = blocklyDocsMap;

export class BlocklyHelpRegistry {
  private readonly docsBaseUrl: string;

  constructor(channel: HandbookChannel = 'live') {
    this.docsBaseUrl = `${handbookBaseUrl(channel)}/Entwickler`;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Zentrale Quelle für alle Block-Hilfetexte und Kategorie-Zuordnungen.
  // Schlüssel = eindeutige Block-ID (uniqueBlockName).
  // Einträge decken alle Standard-Blöcke aller 7 Menügruppen ab.
  // ─────────────────────────────────────────────────────────────────────────
  private readonly helpEntries = new Map<string, BlocklyHelpEntry>([

    // ── Start & Ablauf ─ Steuerung ──────────────────────────────────────────
    ['MY_START_BLOCK', {
      tooltip: 'Startet den Bot-Ablauf, sobald der Bot vollständig geladen ist. Platziere hier alle Blöcke, die beim Start ausgeführt werden sollen. Optionen: „sobald der Bot bereit ist" wartet, bis alle Bot-Daten geladen sind, „sofort" führt den Ablauf ohne Wartezeit sofort aus.',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],
    ['BOT_CONTROLS_RESTART', {
      tooltip: 'Startet den gesamten Bot-Ablauf neu – alle Variablen und der Chatverlauf werden zurückgesetzt.',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],
    ['WAITFOR_SECONDS_NEW', {
      tooltip: 'Wartet die angegebene Anzahl Sekunden, bevor der nächste Block ausgeführt wird.',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],
    ['BOT_CONTROLS_ENDBOT', {
      tooltip: 'Beendet den Bot und zeigt optional eine Abschlussnachricht sowie eine Weiterleitung an.',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],
    ['BOT_NAME', {
      tooltip: 'Gibt den konfigurierten Namen des Bots als Text zurück.',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],
    ['BOT_MODEL', {
      tooltip: 'Gibt das KI-Modell zurück, das für diesen Bot konfiguriert ist.',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],
    ['BOT_TEMPERATURE', {
      tooltip: 'Gibt den Temperature-Wert des Bots zurück (Kreativität der KI-Antworten, 0–2).',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],
    ['RAG_CONDITION_CHAT_HISTORY_EMPTY', {
      tooltip: 'Gibt true zurück, wenn der Chatverlauf leer ist (keine bisherigen Nachrichten). Nützlich für Begrüßungslogik. Optionen: „ist" prüft auf leeren Verlauf, „ist nicht" kehrt die Prüfung um.',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],
    ['INFO_MESSAGECOUNT', {
      tooltip: 'Gibt die Anzahl der Nachrichten im aktuellen Chatverlauf als Zahl zurück.',
      categoryPath: 'Start & Ablauf > Steuerung'
    }],

    // ── Start & Ablauf ─ Stages ─────────────────────────────────────────────
    ['STAGE_GOTO', {
      tooltip: 'Wechselt zur angegebenen Stage. Stages sind Kapitel oder Abschnitte deines Lernmoduls.',
      categoryPath: 'Start & Ablauf > Stages'
    }],
    ['CurrentStage_PROPERITES', {
      tooltip: 'Gibt eine Eigenschaft der aktuell aktiven Stage zurück, z.B. deren Titel oder interne ID.',
      categoryPath: 'Start & Ablauf > Stages'
    }],

    // ── Start & Ablauf ─ Skills ─────────────────────────────────────────────
    ['SKILL_DISABLE', {
      tooltip: 'Deaktiviert den gewählten Skill – der Bot nutzt diesen Skill dann nicht mehr.',
      categoryPath: 'Start & Ablauf > Skills'
    }],
    ['SKILL_ENABLE', {
      tooltip: 'Aktiviert den gewählten Skill wieder.',
      categoryPath: 'Start & Ablauf > Skills'
    }],
    ['SKILL_COUNT_ENABLED', {
      tooltip: 'Gibt die Anzahl der aktuell aktivierten Skills zurück.',
      categoryPath: 'Start & Ablauf > Skills'
    }],
    ['SKILL_COUNT_DISABLED', {
      tooltip: 'Gibt die Anzahl der aktuell deaktivierten Skills zurück.',
      categoryPath: 'Start & Ablauf > Skills'
    }],
    ['SKILL_COUNT_TOTAL', {
      tooltip: 'Gibt die Gesamtanzahl aller Skills (aktiv und inaktiv) zurück.',
      categoryPath: 'Start & Ablauf > Skills'
    }],

    // ── Start & Ablauf ─ Ereignisse ─────────────────────────────────────────
    ['EVENTS_ON_NEW', {
      tooltip: 'Lauscht auf ein Ereignis und führt Aktionen aus, sobald dieses Ereignis ausgelöst wird. Merke dir das Ereignis mit einer ID, um es später verwalten zu können.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_MANAGE', {
      tooltip: 'Aktiviert, deaktiviert oder entfernt ein registriertes Ereignis anhand seiner ID. Optionen: „deaktivieren" pausiert das Ereignis, „aktivieren" reaktiviert es, „entfernen" löscht es endgültig.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_TRIGGER_NEW', {
      tooltip: 'Löst ein Ereignis manuell aus und übergibt optionale Daten. Andere Ereignis-Blöcke können darauf reagieren.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['CALL_LATER', {
      tooltip: 'Führt Aktionen nach einer bestimmten Wartezeit aus, ohne den restlichen Ablauf zu blockieren.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],

    // ── Chatinhalte & Interaktionen ─ Nachrichten und Texte ─────────────────
    ['BOTMESSAGE_BLOCK', {
      tooltip: 'Sendet eine Bot-Nachricht direkt in den Chatverlauf – ohne KI-Verarbeitung.',
      categoryPath: 'Chatinhalte & Interaktionen > Nachrichten und Texte'
    }],
    ['SEND_PROMPT_TXT', {
      tooltip: 'Sendet einen Prompt an die KI und zeigt die KI-Antwort im Chatverlauf an.',
      categoryPath: 'Chatinhalte & Interaktionen > Nachrichten und Texte'
    }],
    ['SEND_PROMPT_USER_BYPASS', {
      tooltip: 'Sendet eine unsichtbare Nutzernachricht an die KI. Die KI verarbeitet sie, aber der Nutzer sieht sie nicht im Chat.',
      categoryPath: 'Chatinhalte & Interaktionen > Nachrichten und Texte'
    }],
    ['SEND_PROMPT_USERMESSAGE', {
      tooltip: 'Zeigt eine Nachricht als Nutzernachricht im Chatverlauf an – ohne KI-Verarbeitung.',
      categoryPath: 'Chatinhalte & Interaktionen > Nachrichten und Texte'
    }],
    ['HTML_TAG', {
      tooltip: 'Formatiert einen Text mit einem HTML-Tag. Optionen: „Fett" und „Kursiv" heben den Text hervor, „Unterstrichen" unterstreicht ihn, „Überschrift 1" bis „Überschrift 6" formatieren ihn als Überschrift in absteigender Größe.',
      categoryPath: 'Chatinhalte & Interaktionen > Nachrichten und Texte'
    }],

    // ── Chatinhalte & Interaktionen ─ Mediainhalte ───────────────────────────
    ['CARD_IMG_FROM_BLOCK_NEW', {
      tooltip: 'Zeigt ein Bild als Karte im Chatverlauf an. Wähle ein Bild aus den konfigurierten Bildquellen.',
      categoryPath: 'Chatinhalte & Interaktionen > Mediainhalte'
    }],
    ['CARD_IFRAME', {
      tooltip: 'Bettet eine Webseite als iFrame-Karte in den Chatverlauf ein.',
      categoryPath: 'Chatinhalte & Interaktionen > Mediainhalte'
    }],
    ['CARD_HTML', {
      tooltip: 'Zeigt beliebigen HTML-Inhalt als Karte im Chatverlauf an.',
      categoryPath: 'Chatinhalte & Interaktionen > Mediainhalte'
    }],
    ['CARD_YOUTUBE', {
      tooltip: 'Bettet ein YouTube-Video als Karte in den Chatverlauf ein.',
      categoryPath: 'Chatinhalte & Interaktionen > Mediainhalte'
    }],
    ['CARD_VIMENO', {
      tooltip: 'Bettet ein Vimeo-Video als Karte in den Chatverlauf ein.',
      categoryPath: 'Chatinhalte & Interaktionen > Mediainhalte'
    }],

    // ── Chatinhalte & Interaktionen ─ Nutzer Interaktion ─────────────────────
    ['STAGE_LISTBTN', {
      tooltip: 'Zeigt alle sichtbaren Stages als klickbare Buttons im Chatverlauf an.',
      categoryPath: 'Chatinhalte & Interaktionen > Nutzer Interaktion'
    }],

    // ── Chatinhalte & Interaktionen ─ Overlays ───────────────────────────────
    ['OVERLAY_HTML', {
      tooltip: 'Zeigt HTML-Inhalt als Modal-Overlay über dem Bot oder der Fusion-View an.',
      categoryPath: 'Chatinhalte & Interaktionen > Overlays'
    }],
    ['STAGE_OVERLAY', {
      tooltip: 'Zeigt eine Stage-Auswahl als Modal-Overlay an.',
      categoryPath: 'Chatinhalte & Interaktionen > Overlays'
    }],
    ['OVERLAY_LOADING', {
      tooltip: 'Zeigt eine Ladeanimation als Overlay an. Ideal für Wartezeiten bei längeren Aktionen.',
      categoryPath: 'Chatinhalte & Interaktionen > Overlays'
    }],
    ['OVERLAY_PROMPT_INPUT', {
      tooltip: 'Gibt den Wert zurück, den der Nutzer in ein Prompt-Overlay eingegeben hat.',
      categoryPath: 'Chatinhalte & Interaktionen > Overlays'
    }],

    // ── KI-Funktionen ─ KI-Text ───────────────────────────────────────────────
    ['AITEXT_OPENAI_CHATCOMPLESIONJSON', {
      tooltip: 'Sendet einen Prompt an OpenAI und erwartet eine strukturierte JSON-Antwort. Wähle über das Dropdown-Feld eines der verfügbaren KI-Modelle aus der Liste sowie die Temperature (Kreativität der Antwort).',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],
    ['AITEXT_VLLM_CHATCOMPLESION', {
      tooltip: 'Sendet einen abweichenden Prompt direkt an das vLLM-Modell des Bots.',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],
    ['AITEXT_VLLM_CHATCOMPLESIONJSON', {
      tooltip: 'Sendet einen Prompt an vLLM und erwartet eine strukturierte JSON-Antwort.',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],

    // ── KI-Funktionen ─ KI-Bild ────────────────────────────────────────────
    ['AIIMAGE_GENERATE_DETAILED', {
      tooltip: 'Generiert ein KI-Bild wahlweise mit OpenAI, fal.ai oder Stable Diffusion. Optional mit einem oder mehreren Referenzbildern (Bild-zu-Bild) und wählbarem Seitenverhältnis.',
      categoryPath: 'KI-Funktionen > KI-Bild'
    }],

    // ── KI-Funktionen ─ Strukturierte KI-Analyse ──────────────────────────────
    ['AITEXT_STRUCTURED_RESPONSE', {
      tooltip: 'Sendet einen Prompt an die KI und erwartet eine strukturierte Antwort mit definierten Kriterien und Feldern. Stecke die gewünschten Felder als Blöcke zusammen.',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],
    ['SCHEMA_FIELD_CRITERION', {
      tooltip: 'Definiert ein Kriterium mit Ja/Nein-Antwort und Begründung. Trage einen Schlüssel und eine Frage ein. Die KI antwortet mit isTrue (boolean) und reason (Text).',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],
    ['SCHEMA_FIELD_STRING', {
      tooltip: 'Definiert ein Textfeld im Schema. Die KI gibt einen Freitext zurück.',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],
    ['SCHEMA_FIELD_NUMBER', {
      tooltip: 'Definiert ein Zahlenfeld im Schema. Die KI gibt eine Zahl zurück.',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],
    ['SCHEMA_FIELD_BOOLEAN', {
      tooltip: 'Definiert ein Ja/Nein-Feld im Schema. Die KI gibt true oder false zurück.',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],
    ['AITEXT_STRUCTURED_RESPONSE_FIELD', {
      tooltip: 'Liest ein Feld aus dem Ergebnis einer strukturierten KI-Analyse anhand des Schlüssels (bzw. Pfades wie "user.name") aus und gibt es im gewählten Typ zurück (Text, Zahl, Ja/Nein oder Ist wahr/Begründung eines Kriteriums).',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],

    // ── KI-Funktionen ─ Quiz ──────────────────────────────────────────────────
    ['QUIZ_START_FROM_TEXT', {
      tooltip: 'Startet ein automatisch aus einem Dokument generiertes Quiz. Konfiguriere Fragenanzahl, falsche Antworten und Ereignisaktionen.',
      categoryPath: 'KI-Funktionen > Quiz'
    }],
    ['QUIZ_START_FROM_TEXT_FREETEXT', {
      tooltip: 'Startet ein Quiz, bei dem die Antwort über das normale Prompt-Eingabefeld gegeben und per KI anhand des Dokuments bewertet wird, statt über Buttons.',
      categoryPath: 'KI-Funktionen > Quiz'
    }],
    ['QUIZ_ANSWER_DATA', {
      tooltip: 'Gibt Daten zur aktuellen Quiz-Antwort zurück. Nur innerhalb von Quiz-Ereignissen nutzbar. Optionen: „Gewählte Antwort" die vom Nutzer gegebene Antwort, „Aktuelle Frage" der Fragetext, „War richtig?" ob die Antwort korrekt war, „Die richtige Lösung" die korrekte Antwort, „KI-Begründung" die Begründung der KI (nur im Freitext-Modus).',
      categoryPath: 'KI-Funktionen > Quiz'
    }],
    ['QUIZ_GENERA_DATA', {
      tooltip: 'Gibt allgemeine Quiz-Statistiken zurück, z.B. Anzahl richtiger und falscher Antworten.',
      categoryPath: 'KI-Funktionen > Quiz'
    }],
    ['QUIZ_ADDITIONAL_PROMPT_INFO', {
      tooltip: 'Fügt der KI eine zusätzliche Anweisung zur Frage-Extraktion hinzu.',
      categoryPath: 'KI-Funktionen > Quiz'
    }],
    ['QUIZ_DEBUG_ALL', {
      tooltip: 'Gibt alle aktuellen Quiz-Daten zur Fehlersuche in der Konsole aus.',
      categoryPath: 'KI-Funktionen > Quiz'
    }],
    ['QUIZ_HISTORY_ENABLED', {
      tooltip: 'Legt fest, ob der projektweite Quiz-Frage/Antwort-Verlauf des Nutzers protokolliert und beim nächsten Quiz-Start in den Generierungs-Prompt eingebettet wird. Optionen: „ja" aktiviert die Protokollierung, „nein" deaktiviert sie.',
      categoryPath: 'KI-Funktionen > Quiz'
    }],
    ['QUIZ_HISTORY_LIMIT_SET', {
      tooltip: 'Legt fest, wie viele der letzten Quiz-Verlaufseinträge in den Generierungs-Prompt eingebettet werden (0 = alle).',
      categoryPath: 'KI-Funktionen > Quiz'
    }],
    ['QUIZ_HISTORY_CLEAR', {
      tooltip: 'Löscht den kompletten projektweiten Quiz-Verlauf des aktuellen Nutzers.',
      categoryPath: 'KI-Funktionen > Quiz'
    }],

    // ── Daten & Inhalte ─ Dokumente ───────────────────────────────────
    ['BLOCK_STACK_DOCUMENT_CONTENT', {
      tooltip: 'Gibt den Textinhalt (Markdown) eines konfigurierten Dokuments zurück. Wähle aus der Liste eines der im Bot vorhandenen Dokumente aus.',
      categoryPath: 'Daten & Inhalte > Dokumente'
    }],

    ['BLOCK_STACK_IMAGE', {
      tooltip: 'Gibt die URL eines konfigurierten Bildes zurück. Wähle aus der Liste eines der im Bot vorhandenen Bilder aus.',
      categoryPath: 'Daten & Inhalte > Dokumente'
    }],
    ['DOC_SEARCH_NEW_SEARC_IN_DOCS', {
      tooltip: 'Durchsucht eine Liste von Dokumenten nach einem Suchbegriff. Optionen: „Suchanfrage liefert Ergebnisse" gibt Ja/Nein zurück, „Ergebnisse als Tabelle" formatiert die Treffer als Tabelle, „Ergebnisse als Text" gibt sie als Fließtext zurück, „Anzahl der Ergebnisse" liefert nur die Trefferzahl, „Suchantwort als JSON-Objekt" liefert die Rohdaten zur Weiterverarbeitung.',
      categoryPath: 'Daten & Inhalte > Dokumente'
    }],
    ['DOC_LIST_JOINING', {
      tooltip: 'Erstellt eine Dokumentenliste aus mehreren Dokumenten. Wird als Eingabe für die Dokumentensuche verwendet.',
      categoryPath: 'Daten & Inhalte > Dokumente'
    }],

    // ── Daten & Inhalte ─ Bausteine ───────────────────────────────────────────
    ['HTML_TAG', {
      tooltip: 'Formatiert einen Text mit einem HTML-Tag. Optionen: „Fett" und „Kursiv" heben den Text hervor, „Unterstrichen" unterstreicht ihn, „Überschrift 1" bis „Überschrift 6" formatieren ihn als Überschrift in absteigender Größe.',
      categoryPath: 'Daten & Inhalte > Bausteine'
    }],
    ['_IMAGE_URL_BY_SKILL_ID', {
      tooltip: 'Gibt die öffentliche URL eines Bildes anhand seiner Skill-ID zurück.',
      categoryPath: 'Daten & Inhalte > Bausteine'
    }],
    ['TEMPLATEVAR_TEXT', {
      tooltip: 'Gibt den Wert einer konfigurierten Text-Inhaltsvariable zurück. Wähle über das Dropdown-Feld eine der im Bot hinterlegten Text-Inhaltsvariablen aus.',
      categoryPath: 'Daten & Inhalte > Bausteine'
    }],
    ['TEMPLATEVAR_NUMBER', {
      tooltip: 'Gibt den Wert einer konfigurierten Zahlen-Inhaltsvariable zurück. Wähle über das Dropdown-Feld eine der im Bot hinterlegten Zahlen-Inhaltsvariablen aus.',
      categoryPath: 'Daten & Inhalte > Bausteine'
    }],
    ['TEMPLATEVAR_BOOLEAN', {
      tooltip: 'Gibt den Wert einer konfigurierten Boolean-Inhaltsvariable zurück. Wähle über das Dropdown-Feld eine der im Bot hinterlegten Ja/Nein-Inhaltsvariablen aus.',
      categoryPath: 'Daten & Inhalte > Bausteine'
    }],

    // ── Daten & Inhalte ─ Datenspeicher ──────────────────────────────────────
    ['KEYVAL_SET_V2', {
      tooltip: 'Speichert einen Wert unter einem Schlüssel – wahlweise botbezogen oder projektbezogen. Optionen: „Bot" speichert nur für diesen Bot, „Projekt" speichert projektweit, für alle Bots des Projekts zugänglich.',
      categoryPath: 'Daten & Inhalte > Datenspeicher'
    }],
    ['KEYVAL_GET_V2', {
      tooltip: 'Liest einen gespeicherten Wert anhand seines Schlüssels aus. Optionen: „Bot" liest aus dem bot-eigenen Speicher, „Projekt" liest aus dem projektweiten Speicher.',
      categoryPath: 'Daten & Inhalte > Datenspeicher'
    }],
    ['KEYVAL_UNIQUE_V2', {
      tooltip: 'Fügt einen Wert eindeutig zu einer Gruppe hinzu – verhindert Duplikate. Optionen: „Bot" speichert nur für diesen Bot, „Projekt" speichert projektweit, für alle Bots des Projekts zugänglich.',
      categoryPath: 'Daten & Inhalte > Datenspeicher'
    }],
    ['KEYVAL_GET_COLLECTION_VALUES_V2', {
      tooltip: 'Gibt alle Werte einer Gruppe als zusammengeführten Text zurück. Optionen: „Bot" liest aus dem bot-eigenen Speicher, „Projekt" liest aus dem projektweiten Speicher.',
      categoryPath: 'Daten & Inhalte > Datenspeicher'
    }],
    ['KEYVAL_GET_COLLECTION_TEXT_V2', {
      tooltip: 'Gibt alle Werte einer Gruppe als zeilengetrennten Text zurück.',
      categoryPath: 'Daten & Inhalte > Datenspeicher'
    }],

    // ── Darstellung ─ Bot-View ────────────────────────────────────────────────
    ['AVATAR_BOT_SET', {
      tooltip: 'Setzt das Avatar-Bild des Bots im Chatverlauf.',
      categoryPath: 'Darstellung > Bot-View'
    }],
    ['AVATAR_USER_SET', {
      tooltip: 'Setzt das Avatar-Bild des Nutzers im Chatverlauf.',
      categoryPath: 'Darstellung > Bot-View'
    }],
    ['AVATAR_CHAT_UNSET', {
      tooltip: 'Entfernt das Avatar-Bild des Bots oder des Nutzers. Optionen: „Nutzer" entfernt das Nutzerbild, „Bot" entfernt das Bot-Bild.',
      categoryPath: 'Darstellung > Bot-View'
    }],
    ['AVATAR_CHAT_FOOTER', {
      tooltip: 'Blendet den Footer des Chats ein oder aus. Optionen: „anzeigen" zeigt ihn wieder an, „ausblenden" versteckt ihn.',
      categoryPath: 'Darstellung > Bot-View'
    }],
    ['AVATAR_CHAT_Header', {
      tooltip: 'Blendet den Header des Chats ein oder aus. Optionen: „anzeigen" zeigt ihn wieder an, „ausblenden" versteckt ihn.',
      categoryPath: 'Darstellung > Bot-View'
    }],

    // ── Darstellung ─ Bot-Avatar ──────────────────────────────────────────────
    ['AVATAR_setBGImg', {
      tooltip: 'Setzt ein Hintergrundbild für den Video-Avatar.',
      categoryPath: 'Darstellung > Bot-Avatar'
    }],
    ['AVATAR_unsetBGImg', {
      tooltip: 'Entfernt das Hintergrundbild des Video-Avatars.',
      categoryPath: 'Darstellung > Bot-Avatar'
    }],
    ['AVATAR_doNotInterrupt', {
      tooltip: 'Verhindert, dass der Nutzer den Avatar durch Sprechen unterbricht. Zeigt Hinweistexte je nach Avatar-Zustand an.',
      categoryPath: 'Darstellung > Bot-Avatar'
    }],
    ['AVATAR_doNotInterruptCancel', {
      tooltip: 'Entfernt alle von „Avatar nicht unterbrechen“ registrierten Ereignisse wieder.',
      categoryPath: 'Darstellung > Bot-Avatar'
    }],

    // ── Darstellung ─ Bot-Audio ───────────────────────────────────────────────
    ['AUDIO_initialMute', {
      tooltip: 'Schaltet das Mikrofon des Nutzers stumm, sobald die Audioverbindung hergestellt wird.',
      categoryPath: 'Darstellung > Bot-Audio'
    }],
    ['AUDIO_MIC_SETTING', {
      tooltip: 'Schaltet das Mikrofon des Nutzers stumm oder aktiviert es. Optionen: „stummschalten" deaktiviert das Mikrofon, „aktivieren" schaltet es wieder ein.',
      categoryPath: 'Darstellung > Bot-Audio'
    }],
    ['AUDIO_doNotInterrupt', {
      tooltip: 'Verhindert, dass der Nutzer den Bot durch Sprechen unterbricht. Zeigt Hinweistexte je nach Bot-Zustand an.',
      categoryPath: 'Darstellung > Bot-Audio'
    }],
    ['AUDIO_doNotInterruptCancel', {
      tooltip: 'Entfernt alle von „Bot nicht unterbrechen“ registrierten Ereignisse wieder.',
      categoryPath: 'Darstellung > Bot-Audio'
    }],

    // ── Darstellung ─ Fusion-View ─────────────────────────────────────────────
    ['SWITCH_FUSION_VIEW', {
      tooltip: 'Wechselt die aktive Fusion-View auf die angegebene View. Wähle aus der Liste eine der im Projekt konfigurierten Fusion-Views aus.',
      categoryPath: 'Darstellung > Fusion-View'
    }],
    ['FV_VIEW_SWITCH', {
      tooltip: 'Wechselt den Anzeigemodus der Fusion-View. Optionen: „Bot im Vollbild" füllt die gesamte Ansicht, „Bot im Fenster Modus" zeigt ihn in einem Fenster, „Bot Floating Ansicht" zeigt ihn schwebend über der Seite, „Side by side Ansicht" zeigt Bot und Seiteninhalt nebeneinander.',
      categoryPath: 'Darstellung > Fusion-View'
    }],
    ['FV_BOT_BTN_VISIBILITY', {
      tooltip: 'Zeigt oder versteckt den Bot-Button in der Fusion-View. Optionen: „Anzeigen" blendet ihn ein, „Verstecken" blendet ihn aus.',
      categoryPath: 'Darstellung > Fusion-View'
    }],
    ['FV_BOT_VISIBILITY', {
      tooltip: 'Zeigt oder versteckt den Bot in der Fusion-View. Optionen: „Anzeigen" blendet ihn ein, „Verstecken" blendet ihn aus.',
      categoryPath: 'Darstellung > Fusion-View'
    }],
    ['LOAD_EXTERNAL_CSS_IN_FUSION', {
      tooltip: 'Lädt eine externe CSS-Datei in die Fusion-View.',
      categoryPath: 'Darstellung > Fusion-View'
    }],
    ['LOAD_EXTERNAL_JS_IN_FUSION', {
      tooltip: 'Lädt eine externe JavaScript-Datei in die Fusion-View.',
      categoryPath: 'Darstellung > Fusion-View'
    }],

    // ── Darstellung ─ Fusion Youtube ─────────────────────────────────────────
    ['YOUTUBE_GO_TO', {
      tooltip: 'Springt zu einer bestimmten Zeitposition im eingebetteten YouTube-Video.',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],
    ['YOUTUBE_SPEED', {
      tooltip: 'Setzt die Wiedergabegeschwindigkeit des YouTube-Videos. Wähle aus der Liste einen Geschwindigkeitsfaktor (z. B. 1 = normale Geschwindigkeit, 2 = doppelte Geschwindigkeit, 0.5 = halbe Geschwindigkeit).',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],
    ['YOUTUBE_VOLUME', {
      tooltip: 'Setzt die Lautstärke des YouTube-Players. Wähle aus der Liste einen Lautstärkewert zwischen 0 (stumm) und 100 (maximale Lautstärke).',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],
    ['YOUTUBE_LOAD_VIDEO', {
      tooltip: 'Lädt ein YouTube-Video anhand seiner Video-ID und setzt optional Start- und Endzeit.',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],
    ['YOUTUBE_EVENT_ON_TIME_HOOK', {
      tooltip: 'Führt Aktionen aus, wenn der YouTube-Player eine bestimmte Zeitposition erreicht. Optionen: „nach" löst aus, sobald die Zeit überschritten wird, „vor" löst aus, solange die Zeit noch nicht erreicht ist, „genau" löst nur bei exakter Übereinstimmung aus.',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],
    ['YOUTUBE_PROPERITES', {
      tooltip: 'Gibt aktuelle Eigenschaften des YouTube-Players zurück, z.B. Position oder Status.',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],

    // ── Darstellung ─ Fusion Vimeo ────────────────────────────────────────────
    ['VIMEO_GO_TO', {
      tooltip: 'Springt zu einer bestimmten Zeitposition im eingebetteten Vimeo-Video.',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],
    ['VIMEO_SPEED', {
      tooltip: 'Setzt die Wiedergabegeschwindigkeit des Vimeo-Videos. Wähle aus der Liste einen Geschwindigkeitsfaktor (z. B. 1 = normale Geschwindigkeit, 2 = doppelte Geschwindigkeit, 0.5 = halbe Geschwindigkeit).',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],
    ['VIMEO_VOLUME', {
      tooltip: 'Setzt die Lautstärke des Vimeo-Players. Wähle aus der Liste einen Lautstärkewert zwischen 0 (stumm) und 100 (maximale Lautstärke).',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],
    ['VIMEO_LOAD_VIDEO', {
      tooltip: 'Lädt ein Vimeo-Video anhand seiner Video-ID.',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],
    ['VIMEO_EVENT_ON_TIME_HOOK', {
      tooltip: 'Führt Aktionen aus, wenn der Vimeo-Player eine bestimmte Zeitposition erreicht. Optionen: „nach" löst aus, sobald die Zeit überschritten wird, „vor" löst aus, solange die Zeit noch nicht erreicht ist, „genau" löst nur bei exakter Übereinstimmung aus.',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],
    ['VIMEO_PROPERITES', {
      tooltip: 'Gibt aktuelle Eigenschaften des Vimeo-Players zurück, z.B. die aktuelle Position.',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],

    // ── Darstellung ─ Fusion Rise ─────────────────────────────────────────────
    ['RISE_PROPERTIES', {
      tooltip: 'Gibt Eigenschaften der aktuellen RISE-Seite zurück, z.B. Seitennummer, Inhalt oder ob es die letzte Seite ist.',
      categoryPath: 'Darstellung > Fusion Rise'
    }],
    ['RISE_GO_TO_PAGE', {
      tooltip: 'Navigiert zu einer bestimmten Seite im RISE-Kurs.',
      categoryPath: 'Darstellung > Fusion Rise'
    }],

    // ── Darstellung ─ Fusion LXT ──────────────────────────────────────────────
    ['LXT_PROPERTIES', {
      tooltip: 'Gibt Eigenschaften der aktuellen LXT-Seite zurück, z.B. Seitennummer oder Inhalt.',
      categoryPath: 'Darstellung > Fusion LXT'
    }],
    ['LXT_GO_TO_PAGE', {
      tooltip: 'Navigiert zu einer bestimmten Seite im LXT-Kurs.',
      categoryPath: 'Darstellung > Fusion LXT'
    }],

    // ── Integrationen ─ On-Site Bot ───────────────────────────────────────────
    ['PARENT_EXEC', {
      tooltip: 'Führt JavaScript-Code auf der Eltern-Seite aus, in die der Bot eingebettet ist.',
      categoryPath: 'Integrationen > On-Site Bot'
    }],
    ['PARENT_get', {
      tooltip: 'Liest einen Wert von der Eltern-Seite aus (z.B. eine JavaScript-Variable).',
      categoryPath: 'Integrationen > On-Site Bot'
    }],
    ['PARENT_NAVIGATE_TO', {
      tooltip: 'Navigiert die Eltern-Seite zu einer anderen URL.',
      categoryPath: 'Integrationen > On-Site Bot'
    }],
    ['PARENT_OPEN_BOT', {
      tooltip: 'Öffnet den On-Site-Bot programmatisch.',
      categoryPath: 'Integrationen > On-Site Bot'
    }],
    ['PARENT_CLOSE_BOT', {
      tooltip: 'Schließt den On-Site-Bot programmatisch.',
      categoryPath: 'Integrationen > On-Site Bot'
    }],
    ['PARENT_RECREATE_BOT_STATE', {
      tooltip: 'Stellt den letzten Öffnen/Schließen-Zustand des On-Site-Bots wieder her.',
      categoryPath: 'Integrationen > On-Site Bot'
    }],
    ['PARENT_LOCATION_X', {
      tooltip: 'Gibt einen Teil der aktuellen URL der Eltern-Seite zurück. Optionen: „href" die vollständige Adresse, „hash" der Anker-Teil nach dem #-Zeichen, „search" die Parameter nach dem ?-Zeichen (Query-String).',
      categoryPath: 'Integrationen > On-Site Bot'
    }],
    ['PARENT_PARAM_BY_BAME', {
      tooltip: 'Liest einen URL-Parameter der Eltern-Seite anhand seines Namens aus.',
      categoryPath: 'Integrationen > On-Site Bot'
    }],

    // ── Integrationen ─ Scores ────────────────────────────────────────────────
    ['SCORE_INIT', {
      tooltip: 'Initialisiert das Score-System. Muss ausgeführt werden, bevor andere Score-Blöcke genutzt werden.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_ACHIEVE', {
      tooltip: 'Markiert ein Achievement als erfüllt und erhöht den Score um den angegebenen Wert. Option „autom. Benachrichtigung": „Ja" zeigt automatisch eine Erfolgsmeldung an, „Nein" unterdrückt sie.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_RESET', {
      tooltip: 'Setzt den einfachen Score auf 0 zurück.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_ADD', {
      tooltip: 'Erhöht den Score um den angegebenen Wert.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_SUB', {
      tooltip: 'Verringert den Score um den angegebenen Wert.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_SET', {
      tooltip: 'Setzt den Score auf einen bestimmten Wert.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_ACHIVEMENTS', {
      tooltip: 'Gibt alle Achievements als JSON oder als Text zurück.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_GET', {
      tooltip: 'Gibt den aktuellen Score-Wert zurück.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_DB_ACHIEVE', {
      tooltip: 'Markiert ein Achievement in einer benannten Score-Datenbank als erfüllt. Option „autom. Benachrichtigung": „Ja" zeigt automatisch eine Erfolgsmeldung an, „Nein" unterdrückt sie.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_DB_RESET', {
      tooltip: 'Setzt eine benannte Score-Datenbank zurück.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_DB_ADD', {
      tooltip: 'Erhöht den Wert in einer benannten Score-Datenbank.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_DB_SUB', {
      tooltip: 'Verringert den Wert in einer benannten Score-Datenbank.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_DB_SET', {
      tooltip: 'Setzt den Wert in einer benannten Score-Datenbank.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_DB_ACHIVEMENTS', {
      tooltip: 'Gibt Achievements aus einer benannten Score-Datenbank zurück. Optionen: „JSON" liefert die Rohdaten als JSON-Struktur, „Text" liefert sie als lesbaren Text.',
      categoryPath: 'Integrationen > Scores'
    }],
    ['SCORE_DB_GET', {
      tooltip: 'Gibt den aktuellen Wert aus einer benannten Score-Datenbank zurück.',
      categoryPath: 'Integrationen > Scores'
    }],

    // ── Logik & Code ─ Bedingungen ────────────────────────────────────────────
    ['RAG_IF', {
      tooltip: 'Prüft eine Bedingung und führt die enthaltenen Aktionen nur dann aus, wenn sie erfüllt ist.',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_IF_ELSE', {
      tooltip: 'Prüft eine Bedingung und führt je nach Ergebnis den Dann- oder den Sonst-Zweig aus.',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_CONDITION_STARTWITH', {
      tooltip: 'Gibt true zurück, wenn der erste Text mit dem zweiten Text beginnt.',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_CONDITION_ENDSWITH', {
      tooltip: 'Gibt true zurück, wenn der erste Text mit dem zweiten Text endet.',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_CONDITION_INCLUDES', {
      tooltip: 'Gibt true zurück, wenn der erste Text den zweiten Text enthält.',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_CONDITION', {
      tooltip: 'Vergleicht zwei Werte auf Gleichheit oder Ungleichheit. Optionen: „ist gleich" prüft auf Übereinstimmung, „ist nicht gleich" prüft auf Abweichung.',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_CONDITION_NUM', {
      tooltip: 'Vergleicht zwei Zahlen. Optionen: „ist größer als", „ist größer als oder gleich", „ist kleiner als", „ist kleiner als oder gleich".',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_OPERATION_NOT', {
      tooltip: 'Negiert eine Bedingung (true wird false, false wird true).',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_OPERATION_AND', {
      tooltip: 'Verknüpft zwei Bedingungen: beide müssen true sein.',
      categoryPath: 'Logik & Code > Bedingungen'
    }],
    ['RAG_OPERATION_OR', {
      tooltip: 'Verknüpft zwei Bedingungen: mindestens eine muss true sein.',
      categoryPath: 'Logik & Code > Bedingungen'
    }],

    // ── Logik & Code ─ Aktionen & Notizen ────────────────────────────────────
    ['GROUP', {
      tooltip: 'Gruppiert mehrere Blöcke mit einer Notiz. Dient der Übersichtlichkeit.',
      categoryPath: 'Logik & Code > Aktionen & Notizen'
    }],
    ['COMMENT', {
      tooltip: 'Fügt eine Notiz in den Ablauf ein. Wird nicht ausgeführt.',
      categoryPath: 'Logik & Code > Aktionen & Notizen'
    }],
    ['OWN_FUNCTION_DEFINITION', {
      tooltip: 'Definiert eine eigene wiederverwendbare Aktion. Gib ihr einen Namen, um sie später per "Aktion ausführen" aufzurufen.',
      categoryPath: 'Logik & Code > Aktionen & Notizen'
    }],
    ['OWN_FUNCTION_CALL', {
      tooltip: 'Führt eine zuvor definierte eigene Aktion anhand ihres Namens aus.',
      categoryPath: 'Logik & Code > Aktionen & Notizen'
    }],

    // ── Logik & Code ─ Dialog Recorder ────────────────────────────────────────
    ['DIALOGRECORDER_actions', {
      tooltip: 'Steuert den Dialog-Recorder: starten, stoppen oder zurücksetzen. Optionen: „Start" beginnt die Aufzeichnung, „Stop" beendet sie, „zurücksetzen" löscht den bisher aufgezeichneten Dialog.',
      categoryPath: 'Logik & Code > Dialog Recorder'
    }],
    ['DIALOGRECORDER_actions_NEW', {
      tooltip: 'Steuert einen benannten Dialog-Recorder: starten, stoppen oder zurücksetzen. Optionen: „Starten" beginnt die Aufzeichnung, „Stoppen" beendet sie, „Zurücksetzen" löscht den bisher aufgezeichneten Dialog. Über "Merken als" vergibst du eine ID, um mehrere Recorder auseinanderzuhalten.',
      categoryPath: 'Logik & Code > Dialog Recorder'
    }],
    ['DIALOGRECORDER_OVERRIDE_USER_NAMES', {
      tooltip: 'Überschreibt die Anzeigenamen von Nutzer und Bot im aufgezeichneten Dialog.',
      categoryPath: 'Logik & Code > Dialog Recorder'
    }],
    ['DIALOGRECORDER_getDialogHtml', {
      tooltip: 'Gibt den aufgezeichneten Dialog als Text, HTML oder JSON zurück. Optionen: „Text" liefert reinen Text, „HTML" liefert formatiertes HTML zur Anzeige, „Json" liefert die Rohdaten als JSON-Struktur zur Weiterverarbeitung.',
      categoryPath: 'Logik & Code > Dialog Recorder'
    }],

    // ── Logik & Code ─ Extras ─────────────────────────────────────────────────
    ['CONFETTI', {
      tooltip: 'Startet eine Konfetti-Animation. Perfekt für Erfolgserlebnisse.',
      categoryPath: 'Logik & Code > Extras'
    }],
    ['CONFETTI_TEXT', {
      tooltip: 'Startet eine Konfetti-Animation mit einem benutzerdefinierten Emoji oder Text und Einstellungen für Größe, Anzahl und Schwerkraft.',
      categoryPath: 'Logik & Code > Extras'
    }],

    // ── Logik & Code ─ Code ───────────────────────────────────────────────────
    ['RAG_RAW', {
      tooltip: 'Führt beliebigen JavaScript-Code direkt aus. Nur für erfahrene Nutzer.',
      categoryPath: 'Logik & Code > Code'
    }],
    ['RAG_RAW_OUTPUT', {
      tooltip: 'Führt JavaScript-Code aus und gibt das Ergebnis als Wert zurück.',
      categoryPath: 'Logik & Code > Code'
    }],
    ['JSON_STRINGIFY', {
      tooltip: 'Wandelt ein JavaScript-Objekt in einen JSON-String um (JSON.stringify).',
      categoryPath: 'Logik & Code > Code'
    }],
    ['JSON_PARSE', {
      tooltip: 'Wandelt einen JSON-String in ein JavaScript-Objekt um (JSON.parse).',
      categoryPath: 'Logik & Code > Code'
    }],
    ['JSON_GET_PROPERTY', {
      tooltip: 'Liest einen Wert aus einer JSON-Antwort anhand eines Eigenschaftsnamens oder Pfades (z.B. "name" oder "user.adresse.stadt").',
      categoryPath: 'Logik & Code > Code'
    }],
    ['CONSOLE.log', {
      tooltip: 'Gibt einen Wert in der Browser-Konsole aus. Nur für Debugging-Zwecke.',
      categoryPath: 'Logik & Code > Code'
    }],
    ['ALERT', {
      tooltip: 'Zeigt einen Browser-Alert-Dialog an. Nur für Debugging-Zwecke.',
      categoryPath: 'Logik & Code > Code'
    }],
    ['DEBUG_CARD', {
      tooltip: 'Gibt ein Objekt als formatierte Debug-Karte im Chatverlauf aus. Nützlich zur Fehlersuche.',
      categoryPath: 'Logik & Code > Code'
    }],
    ['DEBUG_TOASTR', {
      tooltip: 'Zeigt eine Debug-Benachrichtigung als Toastr-Meldung an.',
      categoryPath: 'Logik & Code > Code'
    }],
    ['LOAD_EXTERNAL_CSS_IN_BOT', {
      tooltip: 'Lädt eine externe CSS-Datei in die Bot-Ansicht.',
      categoryPath: 'Logik & Code > Code'
    }],
    ['LOAD_EXTERNAL_JS_IN_BOT', {
      tooltip: 'Lädt eine externe JavaScript-Datei in die Bot-Ansicht.',
      categoryPath: 'Logik & Code > Code'
    }],
    ['DEBUG_LOG_ENTRY', {
      tooltip: 'Schreibt einen eigenen Eintrag mit Titel und Grund in das Debug-Protokoll (siehe "Bot testen"). Hilfreich, um beim Testen nachzuvollziehen, an welcher Stelle im Ablauf du dich gerade befindest.',
      categoryPath: 'Logik & Code > Code'
    }],

    // ── Helfer ────────────────────────────────────────────────────────────────
    ['HELPER_ADDSTAT', {
      tooltip: 'Erstellt einen Statistik-Messpunkt, der in der Nutzerstatistik erfasst und ausgewertet wird.',
      categoryPath: 'Logik & Code > Helfer'
    }],
    ['HELPER_COPYTOCLIPBOARD', {
      tooltip: 'Kopiert einen Text in die Zwischenablage des Nutzers.',
      categoryPath: 'Logik & Code > Helfer'
    }],
    ['HELPER_CLOSEWINDOW', {
      tooltip: 'Schließt das aktuelle Browser-Fenster.',
      categoryPath: 'Logik & Code > Helfer'
    }],
    ['HELPER_DOWNLOADTEXTASFILE', {
      tooltip: 'Lässt den Nutzer eine Textdatei herunterladen.',
      categoryPath: 'Logik & Code > Helfer'
    }],
    ['HELPER_DOWNLOADMATERIAL', {
      tooltip: 'Lädt ein hinterlegtes Material anhand seiner Skill-ID herunter.',
      categoryPath: 'Logik & Code > Helfer'
    }],
    ['HELPER_CURRENTLANGUAGE', {
      tooltip: 'Liefert die aktuelle Sprache des Bots (z. B. "de", "en").',
      categoryPath: 'Logik & Code > Helfer'
    }],

    // ── Bot-Avatar ──────────────────────────────────────────────────────────
    ['AVATAR_addFooterButton', {
      tooltip: 'Fügt dem Avatar einen zusätzlichen Button im Footer-Bereich hinzu, der bei Klick eine von dir festgelegte Aktion ausführt. Praktisch für schnelle Auswahlmöglichkeiten direkt unter dem Avatar-Video.',
      categoryPath: 'Darstellung > Bot-Avatar'
    }],
    ['AVATAR_clearFooterButtons', {
      tooltip: 'Entfernt alle zuvor über "Footer-Button hinzufügen" hinzugefügten Buttons des Avatars wieder.',
      categoryPath: 'Darstellung > Bot-Avatar'
    }],
    ['AVATAR_stop', {
      tooltip: 'Bricht sofort ab, was der Avatar gerade tut – egal ob er noch nachdenkt oder bereits spricht. Nützlich, um den Avatar z. B. bei einer bestimmten Nutzereingabe sofort zu unterbrechen.',
      categoryPath: 'Darstellung > Bot-Avatar'
    }],

    // ── Nachrichten und Texte ───────────────────────────────────────────────
    ['CAST_TO_TEXT', {
      tooltip: 'Wandelt einen beliebigen Wert (z. B. eine Zahl oder ein Ja/Nein) in Text um, damit er mit Textbausteinen wie "Text verbinden" kombiniert werden kann.',
      categoryPath: 'Chatinhalte & Interaktionen > Nachrichten und Texte'
    }],

    // ── Nutzer Interaktion ──────────────────────────────────────────────────
    ['RAGAI_BUTTON_OUTPUT', {
      tooltip: 'Erstellt einen einzelnen Button mit Beschriftung und einer bei Klick auszuführenden Aktion, zur Verwendung innerhalb eines Buttons-Bausteins (z. B. Bot-Nachricht mit Buttons).',
      categoryPath: 'Chatinhalte & Interaktionen > Nutzer Interaktion'
    }],
    ['RAGAI_BUTTON_OUTPUT_IF', {
      tooltip: 'Wie "Button", zeigt den Button aber nur an, wenn die angegebene Bedingung erfüllt ist – ansonsten wird kein Button erzeugt.',
      categoryPath: 'Chatinhalte & Interaktionen > Nutzer Interaktion'
    }],

    // ── Overlays ────────────────────────────────────────────────────────────
    ['OVERLAY_PROMPT', {
      tooltip: 'Zeigt ein Overlay mit einem Eingabefeld und Buttons an. Nutze stattdessen die aktuellen Bausteine unter "Overlay Vorlagen".',
      categoryPath: 'Chatinhalte & Interaktionen > Overlays'
    }],
    ['OVERLAY_TEXT', {
      tooltip: 'Zeigt ein einfaches Text-Overlay ohne Buttons an. Nutze stattdessen die aktuellen Bausteine unter "Overlay Vorlagen".',
      categoryPath: 'Chatinhalte & Interaktionen > Overlays'
    }],
    ['OVERLAY_TEXT_BTN', {
      tooltip: 'Zeigt ein Overlay mit Text und Buttons an. Nutze stattdessen die aktuellen Bausteine unter "Overlay Vorlagen".',
      categoryPath: 'Chatinhalte & Interaktionen > Overlays'
    }],

    // ── Ereignisse ──────────────────────────────────────────────────────────
    ['TIME_SECONDS', {
      tooltip: 'Wandelt eine Uhrzeit-Angabe (Stunden, Minuten, Sekunden) in eine Gesamtzahl an Sekunden um – wird z. B. bei zeitbasierten Fusion-View-Ereignissen als Eingabe benötigt.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_DISABLE', {
      tooltip: 'Deaktiviert eine über "Merken als" registrierte Ereignis-Überwachung, ohne sie zu entfernen – sie kann später wieder aktiviert werden.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_ENABLE', {
      tooltip: 'Aktiviert eine zuvor deaktivierte Ereignis-Überwachung wieder.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_OFF', {
      tooltip: 'Entfernt eine über "Merken als" registrierte Ereignis-Überwachung endgültig – sie reagiert danach nicht mehr auf das Ereignis.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_TRIGGER', {
      tooltip: 'Löst manuell ein Ereignis mit dem angegebenen Namen aus. Alle Blöcke, die auf dieses Ereignis lauschen, werden daraufhin ausgeführt.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_DEBUG', {
      tooltip: 'Gibt Name und Daten des gerade verarbeiteten Ereignisses als Debug-Karte im Chatverlauf aus. Praktisch, um beim Aufbau eigener Ereignis-Logik zu prüfen, welche Daten tatsächlich ankommen.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_DATA_OBJECT_ATTRIBUTE', {
      tooltip: 'Liest eine einzelne Eigenschaft aus den Daten des gerade verarbeiteten Ereignisses aus, anhand ihres Schlüssels. Nur innerhalb eines "bei Ereignis"-Blocks nutzbar.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_DATA_OBJECT', {
      tooltip: 'Gibt entweder den Namen oder die Daten des gerade verarbeiteten Ereignisses zurück. Nur innerhalb eines "bei Ereignis"-Blocks nutzbar. Optionen: „Name" liefert den Ereignisnamen, „Daten" liefert die mit dem Ereignis übergebenen Zusatzdaten.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],

    // ── Skills ──────────────────────────────────────────────────────────────
    ['SKILL_ENABLECHILD', {
      tooltip: 'Aktiviert den gewählten Skill sowie alle untergeordneten Skills bis zur angegebenen Tiefe. Praktisch für Skill-Gruppen mit mehreren Unterpunkten.',
      categoryPath: 'Start & Ablauf > Skills'
    }],
    ['SKILL_DISABLECHILD', {
      tooltip: 'Deaktiviert den gewählten Skill sowie alle untergeordneten Skills bis zur angegebenen Tiefe. Praktisch für Skill-Gruppen mit mehreren Unterpunkten.',
      categoryPath: 'Start & Ablauf > Skills'
    }],

    // ── Stages ──────────────────────────────────────────────────────────────
    ['STAGE_HIDE_SHOW', {
      tooltip: 'Blendet die gewählte Stage in der Stage-Navigation ein oder aus, ohne sie zu löschen. Optionen: „anzeigen" macht sie wieder sichtbar, „verbergen" blendet sie aus.',
      categoryPath: 'Start & Ablauf > Stages'
    }],
    ['STAGE_DELETE', {
      tooltip: 'Entfernt die gewählte Stage dauerhaft aus dem Bot. Bereits gespeicherte Verweise auf diese Stage funktionieren danach nicht mehr.',
      categoryPath: 'Start & Ablauf > Stages'
    }],

    // ── Wenn-Dann ───────────────────────────────────────────────────────────
    ['IFTHEN_COUNTER', {
      tooltip: 'Gibt zurück, wie oft der gewählte Skill bereits ausgelöst wurde. Nur innerhalb eines Wenn-Dann-Skills nutzbar, z. B. um beim dritten Versuch anders zu reagieren.',
      categoryPath: 'Wenn-Dann'
    }],

    // ── Bot-to-Bot-Nachricht (Gruppenchat) ──────────────────────────────────
    ['GROUPCHAT_SEND', {
      tooltip: 'Sendet eine Nachricht in einen Gruppenchat-Kanal, die von anderen Bots (oder demselben Bot in einer anderen Sitzung) gelesen werden kann. Ermöglicht Bot-zu-Bot-Kommunikation.',
      categoryPath: 'KI-Funktionen > Bot-to-Bot-Nachricht'
    }],
    ['GROUPCHAT_READ', {
      tooltip: 'Liest die bisherigen Nachrichten eines Gruppenchat-Kanals aus.',
      categoryPath: 'KI-Funktionen > Bot-to-Bot-Nachricht'
    }],
    ['GROUPCHAT_DELETE', {
      tooltip: 'Löscht alle bisherigen Nachrichten in einem Gruppenchat-Kanal unwiderruflich.',
      categoryPath: 'KI-Funktionen > Bot-to-Bot-Nachricht'
    }],

    // ── Dokumente ───────────────────────────────────────────────────────────
    ['DOC_LIST_JOINING_DOC', {
      tooltip: 'Einzelner Eintrag für eine Dokumentenliste – wird an den Baustein "Dokumenten Liste" angehängt, um mehrere Dokumente für die Dokumentensuche zusammenzustellen.',
      categoryPath: 'Daten & Inhalte > Dokumente'
    }],

    // ── Up/Download ─────────────────────────────────────────────────────────
    ['TEMPLATE_VALUE', {
      tooltip: 'Ersetzt einen Platzhalter in einer Dokumentvorlage (z. B. einem PDF- oder Word-Template) durch den angegebenen Wert. Mehrere dieser Bausteine können kombiniert werden, um mehrere Platzhalter zu befüllen.',
      categoryPath: 'Daten & Inhalte > Up/Download'
    }],

    // ── On-Site Bot ─────────────────────────────────────────────────────────
    ['PARENT_GET_TEXT', {
      tooltip: 'Liest den sichtbaren Text der Seite, in die der On-Site-Bot eingebettet ist, und gibt ihn als Bot-Nachricht im Chat aus. Nur für On-Site-Bots relevant.',
      categoryPath: 'Integrationen > On-Site Bot'
    }],

    // ── Mail ────────────────────────────────────────────────────────────────
    ['MAIL_SEND_DIRECT', {
      tooltip: 'Versendet direkt eine E-Mail mit Empfänger (An/CC/BCC), Betreff sowie Text- und HTML-Inhalt – ganz ohne eigenen Mail-Webhook.',
      categoryPath: 'Integrationen > Mail'
    }],
    ['MAIL_ENVELOPE', {
      tooltip: 'Erstellt einen vorbereiteten E-Mail-Umschlag mit Empfänger, Betreff und Inhalt, der anschließend über einen Mail-Webhook versendet werden kann. Nutze stattdessen den Baustein "Mail senden".',
    }],
    ['MAIL_SEND_WEBHOOK', {
      tooltip: 'Versendet eine zuvor mit "Mail" erstellte Nachricht über einen konfigurierten Mail-Webhook. Nutze stattdessen den Baustein "Mail senden".',
    }],

    // ── Fusion Vimeo ────────────────────────────────────────────────────────
    ['VIMEO_LOOP', {
      tooltip: 'Aktiviert oder deaktiviert die automatische Wiederholung (Loop) für das aktuelle Vimeo-Video. Optionen: „aktivieren" startet das Video nach dem Ende erneut, „deaktivieren" stoppt es am Ende.',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],

    // ── Webhook (intern) ────────────────────────────────────────────────────
    ['OPTIONS_DUMMY_WH', {
      tooltip: 'Interner Platzhalter-Baustein ohne eigene Funktion – wird automatisch als Standardwert für optionale Webhook-Einstellungen verwendet und muss normalerweise nicht manuell platziert werden.',
      categoryPath: 'Integrationen > Webhook'
    }],
    ['OPTIONS_DUMMY', {
      tooltip: 'Interner Platzhalter-Baustein ohne eigene Funktion – wird automatisch als Standardwert für optionale Einstellungen verwendet und muss normalerweise nicht manuell platziert werden.',
    }],

    // ── Veraltete Blöcke (weitere) ──────────────────────────────────────────
    ['AI_GENERATED_TEXT', {
      tooltip: 'Sendet einen Prompt an ein wählbares KI-Modell und gibt die generierte Antwort als Text zurück, den du weiterverwenden kannst. Nutze stattdessen die Bausteine unter "KI-Text".',
    }],
    ['STRING_JOINING', {
      tooltip: 'Verbindet mehrere Textbausteine zu einem zusammenhängenden Text. Nutze stattdessen den neueren Baustein "Text verbinden".',
    }],
    ['STRING_JOINING_TXT', {
      tooltip: 'Einzelner Text-Eintrag für den alten Textverbinder-Baustein "STRING_JOINING".',
    }],
    ['SKILL_FOR_NEXT_RUN', {
      tooltip: 'Erzwingt, dass die angehängten Skills bei der nächsten Bot-Antwort verwendet werden.',
    }],
    ['SKILL_JOINING', {
      tooltip: 'Einzelner Skill-Eintrag für den Baustein "Diese Skills erzwingen".',
    }],
    ['RAG_IF_2', {
      tooltip: 'Alte Variante des Bedingungs-Bausteins. Nutze stattdessen "prüfe ob ... dann tu" bzw. "prüfe ob ... dann tu ... sonst tu".',
    }],

    // ── Adobe Connect ───────────────────────────────────────────────────────
    ['ADOBECONNECT_FUNCTIONS', {
      tooltip: 'Ruft eine Adobe-Connect-Systeminformation ab, z. B. Benutzername, Rolle, Raum-URL oder Teilnehmerliste – abhängig von der im Dropdown gewählten Funktion.',
      categoryPath: 'Integrationen > Adobe Connect'
    }],
    ['ADOBECONNECT_DISPATCHSYNCMESSAGE', {
      tooltip: 'Sendet eine Synchronisationsnachricht an Adobe Connect, um Zustände zwischen Bot und Meeting-Raum abzugleichen.',
      categoryPath: 'Integrationen > Adobe Connect'
    }],

    // ── SCORM ───────────────────────────────────────────────────────────────
    ['SCORM_GET', {
      tooltip: 'Liest einen SCORM-Wert aus dem Lernmanagementsystem (LMS) aus, z. B. den Namen des Lerners oder den aktuellen Punktestand.',
      categoryPath: 'Integrationen > SCORM'
    }],
    ['SCORM_SET', {
      tooltip: 'Schreibt einen SCORM-Wert in das Lernmanagementsystem (LMS), z. B. um den Lernfortschritt oder Punktestand zu aktualisieren.',
      categoryPath: 'Integrationen > SCORM'
    }],
    ['SCORM_ISIINIT', {
      tooltip: 'Prüft, ob die SCORM-Verbindung zum Lernmanagementsystem bereits initialisiert wurde.',
      categoryPath: 'Integrationen > SCORM'
    }],
    ['SCORM_INIT', {
      tooltip: 'Initialisiert die SCORM-Verbindung zum Lernmanagementsystem. Muss vor anderen SCORM-Aktionen ausgeführt werden.',
      categoryPath: 'Integrationen > SCORM'
    }],
    ['SCORM_COMMIT', {
      tooltip: 'Speichert die bisher gesetzten SCORM-Werte dauerhaft im Lernmanagementsystem.',
      categoryPath: 'Integrationen > SCORM'
    }],
    ['SCORM_FINISH', {
      tooltip: 'Beendet die SCORM-Sitzung und schließt die Verbindung zum Lernmanagementsystem ordnungsgemäß.',
      categoryPath: 'Integrationen > SCORM'
    }],

    // ── KI-Text (weitere) ───────────────────────────────────────────────────
    ['AITEXT_CHATCOMPLETION', {
      tooltip: 'Sendet einen Prompt an ein wählbares KI-Modell und gibt die generierte Antwort als Text zurück, den du z. B. in einer Bot-Nachricht weiterverwenden kannst.',
      categoryPath: 'KI-Funktionen > KI-Text'
    }],
    ['AITEXT_OPENAI_CHATCOMPLESION', {
      tooltip: 'Sendet einen Prompt an OpenAI und gibt die Antwort als Text zurück. Wähle über das Dropdown-Feld eines der verfügbaren KI-Modelle aus der Liste. Nutze stattdessen den aktuellen Baustein "Antwort eines LLM".',
    }],
    ['AITEXT_OPENAI_CHATCOMPLETIONJSON', {
      tooltip: 'Sendet einen Prompt an OpenAI und erwartet eine strukturierte JSON-Antwort. Nutze stattdessen "Strukturierte KI-Analyse".',
    }],

    // ── Webhook (weitere) ───────────────────────────────────────────────────
    ['WEBHOOK_CALL', {
      tooltip: 'Ruft einen konfigurierten Webhook auf und übergibt dabei die angegebenen Daten als JSON-Objekt an das externe System.',
      categoryPath: 'Integrationen > Webhook'
    }],

    // ── WAITFOR (veraltet, siehe Scripting-Ausführungskontexte) ────────────
    ['WAITFOR_NEXTIDLE', {
      tooltip: 'Wartet, bis der Bot nach der aktuellen Aktion wieder bereit (im Ruhezustand) ist, bevor der nächste Schritt ausgeführt wird.',
    }],
    ['WAITFOR_INITIDLE', {
      tooltip: 'Wartet, bis der Bot beim Start vollständig geladen und einsatzbereit ist.',
    }],
    ['WAITFOR_SCORMINITED', {
      tooltip: 'Wartet, bis die SCORM-Verbindung zum Lernmanagementsystem initialisiert wurde.',
    }],
    ['WAITFOR_INITVARS', {
      tooltip: 'Wartet, bis alle Variablen des Bots initial geladen wurden.',
    }],
    ['WAITFOR_EVENT', {
      tooltip: 'Wartet, bis das ausgewählte Ereignis einmalig ausgelöst wurde, bevor der nächste Schritt ausgeführt wird.',
    }],
    ['WAITFOR_SECONDS', {
      tooltip: 'Wartet die angegebene Anzahl Sekunden, bevor der nächste Schritt ausgeführt wird.',
    }],
    ['WAITFOR_MINUTES', {
      tooltip: 'Wartet die angegebene Anzahl Minuten, bevor der nächste Schritt ausgeführt wird.',
    }],

    // ── Datenspeicher (veraltet, siehe aktuelle "Datenspeicher"-Bausteine) ─
    ['KEYVAL_SET', {
      tooltip: 'Speichert einen Wert unter einem Schlüssel im bot-eigenen Datenspeicher. Nutze stattdessen den aktuellen Baustein unter "Datenspeicher".',
    }],
    ['KEYVAL_UNIQUE', {
      tooltip: 'Fügt einen Wert eindeutig zu einer Gruppe im bot-eigenen Datenspeicher hinzu – verhindert Duplikate.',
    }],
    ['KEYVAL_GET', {
      tooltip: 'Liest einen gespeicherten Wert anhand seines Schlüssels aus dem bot-eigenen Datenspeicher aus.',
    }],
    ['KEYVAL_GET_COLLECTION_VALUES', {
      tooltip: 'Gibt alle Werte einer Gruppe aus dem bot-eigenen Datenspeicher als Liste zurück.',
    }],
    ['KEYVAL_GET_COLLECTION_TEXT', {
      tooltip: 'Gibt alle Werte einer Gruppe aus dem bot-eigenen Datenspeicher als zeilengetrennten Text zurück.',
    }],
    ['PROJECTKEYVAL_SET', {
      tooltip: 'Speichert einen Wert unter einem Schlüssel im projektweiten Datenspeicher. Nutze stattdessen den aktuellen Baustein unter "Datenspeicher" mit der Auswahl "Projekt".',
    }],
    ['PROJECTKEYVAL_UNIQUE', {
      tooltip: 'Fügt einen Wert eindeutig zu einer Gruppe im projektweiten Datenspeicher hinzu – verhindert Duplikate.',
    }],
    ['PROJECTKEYVAL_GET', {
      tooltip: 'Liest einen gespeicherten Wert anhand seines Schlüssels aus dem projektweiten Datenspeicher aus.',
    }],
    ['PROJECTKEYVAL_GET_COLLECTION_VALUES', {
      tooltip: 'Gibt alle Werte einer Gruppe aus dem projektweiten Datenspeicher als Liste zurück.',
    }],
    ['PROJECTKEYVAL_GET_COLLECTION_TEXT', {
      tooltip: 'Gibt alle Werte einer Gruppe aus dem projektweiten Datenspeicher als zeilengetrennten Text zurück.',
    }],
    ['PROJECTKEYVAL_SET_V2', {
      tooltip: 'Speichert einen Wert unter einem Schlüssel im projektweiten Datenspeicher. Nutze stattdessen den aktuellen Baustein unter "Datenspeicher" mit der Auswahl "Projekt".',
    }],
    ['PROJECTKEYVAL_UNIQUE_V2', {
      tooltip: 'Fügt einen Wert eindeutig zu einer Gruppe im projektweiten Datenspeicher hinzu.',
    }],
    ['PROJECTKEYVAL_GET_V2', {
      tooltip: 'Liest einen gespeicherten Wert anhand seines Schlüssels aus dem projektweiten Datenspeicher aus.',
    }],
    ['PROJECTKEYVAL_GET_COLLECTION_VALUES_V2', {
      tooltip: 'Gibt alle Werte einer Gruppe aus dem projektweiten Datenspeicher als Liste zurück.',
    }],
    ['PROJECTKEYVAL_GET_COLLECTION_TEXT_V2', {
      tooltip: 'Gibt alle Werte einer Gruppe aus dem projektweiten Datenspeicher als zeilengetrennten Text zurück.',
    }],

    // ── Fusion LXT (weitere) ────────────────────────────────────────────────
    ['LXT_ACTIONS', {
      tooltip: 'Navigiert im eingebetteten LXT-Kurs eine Seite vor oder zurück.',
      categoryPath: 'Darstellung > Fusion LXT'
    }],
    ['LXT_EVENTS', {
      tooltip: 'Reagiert auf ein Ereignis des eingebetteten LXT-Kurses (z. B. Seitenwechsel), ausgewählt über eine übersichtliche Dropdown-Liste.',
      categoryPath: 'Darstellung > Fusion LXT'
    }],
    ['LXT_EVENTS_RAW', {
      tooltip: 'Reagiert auf ein Ereignis des eingebetteten LXT-Kurses, ausgewählt über den technischen Rohnamen des Events.',
      categoryPath: 'Darstellung > Fusion LXT'
    }],

    // ── Fusion Rise (weitere) ───────────────────────────────────────────────
    ['RISE_ACTIONS', {
      tooltip: 'Navigiert im eingebetteten Rise-Kurs eine Seite vor oder zurück.',
      categoryPath: 'Darstellung > Fusion Rise'
    }],
    ['RISE_EVENTS', {
      tooltip: 'Reagiert auf ein Ereignis des eingebetteten Rise-Kurses (z. B. Seitenwechsel), ausgewählt über eine übersichtliche Dropdown-Liste.',
      categoryPath: 'Darstellung > Fusion Rise'
    }],
    ['RISE_EVENTS_RAW', {
      tooltip: 'Reagiert auf ein Ereignis des eingebetteten Rise-Kurses, ausgewählt über den technischen Rohnamen des Events.',
      categoryPath: 'Darstellung > Fusion Rise'
    }],

    // ── Fusion Youtube (weitere) ────────────────────────────────────────────
    ['YOUTUBE_ACTIONS', {
      tooltip: 'Steuert die Wiedergabe des eingebetteten YouTube-Videos: Abspielen, Pausieren, Stoppen, Stummschalten oder Stummschaltung aufheben.',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],
    ['YOUTUBE_EVENTS', {
      tooltip: 'Reagiert auf ein Ereignis des eingebetteten YouTube-Videos (z. B. Video gestartet oder beendet), ausgewählt über eine übersichtliche Dropdown-Liste.',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],
    ['YOUTUBE_EVENTS_RAW', {
      tooltip: 'Reagiert auf ein Ereignis des eingebetteten YouTube-Videos, ausgewählt über den technischen Rohnamen des Events.',
      categoryPath: 'Darstellung > Fusion Youtube'
    }],

    // ── Fusion Vimeo (weitere) ──────────────────────────────────────────────
    ['VIMEO_ACTIONS', {
      tooltip: 'Steuert die Wiedergabe des eingebetteten Vimeo-Videos: Abspielen oder Pausieren.',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],
    ['VIMEO_EVENTS', {
      tooltip: 'Reagiert auf ein Ereignis des eingebetteten Vimeo-Videos, ausgewählt über eine übersichtliche Dropdown-Liste.',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],
    ['VIMEO_EVENTS_RAW', {
      tooltip: 'Reagiert auf ein Ereignis des eingebetteten Vimeo-Videos, ausgewählt über den technischen Rohnamen des Events.',
      categoryPath: 'Darstellung > Fusion Vimeo'
    }],

    // ── Veraltete Dropdown-Sammelblöcke ─────────────────────────────────────
    ['BOT_CONTROLS', {
      tooltip: 'Sammelblock für grundlegende Bot-Steuerungen (Neustart, Bot-Denken abbrechen). Nutze stattdessen die einzelnen aktuellen Bausteine.',
    }],
    ['OVERLAY_ACTIONS', {
      tooltip: 'Schließt das aktuell offene Overlay. Nutze stattdessen den aktuellen Baustein "Overlay schließen".',
    }],
    ['STAGE_ACTIONS', {
      tooltip: 'Sammelblock für grundlegende Stage-Navigation (vor, zurück, wiederherstellen). Nutze stattdessen die einzelnen aktuellen Stage-Bausteine.',
    }],

    // ── Ereignisse (weitere) ────────────────────────────────────────────────
    ['EVENTS_ON', {
      tooltip: 'Reagiert auf ein Ereignis und führt die angehängten Aktionen aus. Nutze stattdessen den aktuellen Baustein "Globales reagieren auf Ereignis".',
    }],
    ['EVENTS_ON_NEW_ONCE', {
      tooltip: 'Reagiert einmalig auf das nächste Auftreten des gewählten Ereignisses und entfernt sich danach automatisch selbst.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['EVENT_ALL', {
      tooltip: 'Liste aller verfügbaren Ereignisse als übersichtliches Dropdown, zur Auswahl in anderen Ereignis-Bausteinen.',
    }],
    ['EVENT_ALL_RAW', {
      tooltip: 'Liste aller verfügbaren Ereignisse als Dropdown mit den technischen Rohnamen, zur Auswahl in anderen Ereignis-Bausteinen.',
    }],

    // ── Wenn-Dann (weitere) ─────────────────────────────────────────────────
    ['GLOBAL_VAR', {
      tooltip: 'Liest eine globale Bot-Eigenschaft aus. Optionen: „Bot Name" der Name des Bots, „Bot Farbe" die konfigurierte Bot-Farbe, „Thread ID" die ID des aktuellen Gesprächsverlaufs, „Thread Inhalt" der bisherige Gesprächsinhalt, „Thread OpenAi ID" die zugehörige OpenAI-Thread-ID.',
    }],
    ['IFTHEN_SPECIFIC_STRING', {
      tooltip: 'Gibt den Wert einer Text-Variable des aktuell bearbeiteten Wenn-Dann-Skills zurück. Wähle über das Dropdown-Feld eine der für dieses Wenn-Dann konfigurierten Text-Variablen aus.',
    }],
    ['IFTHEN_SPECIFIC_NUMBER', {
      tooltip: 'Gibt den Wert einer Zahlen-Variable des aktuell bearbeiteten Wenn-Dann-Skills zurück. Wähle über das Dropdown-Feld eine der für dieses Wenn-Dann konfigurierten Zahlen-Variablen aus.',
    }],
    ['IFTHEN_SPECIFIC_BOOLEAN', {
      tooltip: 'Gibt den Wert einer Ja/Nein-Variable des aktuell bearbeiteten Wenn-Dann-Skills zurück. Wähle über das Dropdown-Feld eine der für dieses Wenn-Dann konfigurierten Ja/Nein-Variablen aus.',
    }],

    // ── Veraltete Chatinhalte ───────────────────────────────────────────────
    ['CARD_BUTTONS', {
      tooltip: 'Zeigt eine Reihe von Buttons als eigenständige Karte im Chatverlauf an. Nutze stattdessen die aktuellen Button-Bausteine.',
    }],
    ['CARD_IMG_FROM_BLOCK', {
      tooltip: 'Zeigt ein Bild mit Alternativtext und CSS-Klasse im Chatverlauf an. Nutze stattdessen den aktuellen Baustein "Bild".',
    }],
    ['RAGAI_BUTTON', {
      tooltip: 'Erstellt einen Button mit Beschriftung und einer bei Klick auszuführenden Aktion. Nutze stattdessen den aktuellen Button-Baustein.',
    }],
    ['RAGAI_BUTTON_2', {
      tooltip: 'Erstellt einen Button mit Beschriftung und einer bei Klick auszuführenden Aktion. Nutze stattdessen den aktuellen Button-Baustein.',
    }],
    ['RAGAI_BUTTON_TEXT', {
      tooltip: 'Einfacher Button ohne eigene Klick-Aktion, nur mit Beschriftung.',
    }],
    ['RAGAI_BUTTON_TEXT_2', {
      tooltip: 'Einfacher Button ohne eigene Klick-Aktion, nur mit Beschriftung.',
    }],
    ['BOT_WELCOME_MESSAGE', {
      tooltip: 'Zeigt beim allerersten Start des Bots (noch kein Chatverlauf vorhanden) eine statische oder KI-generierte Begrüßungsnachricht an. Nutze stattdessen den aktuellen Baustein "Willkommensnachricht".',
    }],
    ['AIIMAGE_GENERATE_IMAGE_IN_CARD', {
      tooltip: 'Generiert per Stable Diffusion ein KI-Bild aus einer Beschreibung und zeigt es direkt als Karte im Chatverlauf an.',
    }],
    ['AIIMAGE_GENERATE_IMAGE_IN_CARD_NEW', {
      tooltip: 'Generiert wahlweise per Stable Diffusion oder OpenAI ein KI-Bild aus einer Beschreibung und gibt die Bild-URL zurück.',
    }],
    ['BOT_BYPASS_THREAD', {
      tooltip: 'Technischer Sonderbaustein, der den gesamten Chatverlauf über eine eigene Chat-Completion-Anfrage statt über den Standard-Threadverlauf abwickelt. Nur für Spezialfälle gedacht.',
    }],
    ['OVERLAY_UPLOAD_IMAGE_ANALYSE_WITH_IMAGE', {
      tooltip: 'Zeigt ein Overlay zum Hochladen eines Bildes an, zeigt das Bild anschließend im Chat an und lässt es zusätzlich per KI-Prompt analysieren.',
    }],
    ['OVERLAY_UPLOAD_TXT_EASY', {
      tooltip: 'Zeigt ein Overlay zum Hochladen einer Textdatei an; der Dateiinhalt wird anschließend unsichtbar an den Bot übergeben.',
    }],

    // ── Stage (veraltet, weitere) ───────────────────────────────────────────
    ['STAGE_LSITBTN', {
      tooltip: 'Zeigt die verfügbaren Stages als klickbare Auswahl-Buttons im Chatverlauf an. Nutze stattdessen den aktuellen Baustein "Stage-Auswahl".',
    }],

    // ── Up/Download (weitere) ───────────────────────────────────────────────
    ['RAGAI_DOWNLOAD', {
      tooltip: 'Lässt den Nutzer eine Textdatei mit selbst festgelegtem Inhalt und Dateinamen herunterladen.',
      categoryPath: 'Daten & Inhalte > Up/Download'
    }],
    ['RAGAI_DOWNLOAD_TEMPLATE', {
      tooltip: 'Füllt eine hinterlegte PDF-Vorlage mit den zuvor gesetzten Platzhalterwerten und lässt sie den Nutzer als Datei herunterladen.',
    }],
    ['UPLOADFILE_PROPERITES', {
      tooltip: 'Liest eine Eigenschaft der zuletzt vom Nutzer hochgeladenen Datei aus.',
      categoryPath: 'Daten & Inhalte > Up/Download'
    }],

    // ── Toastr-Benachrichtigungen (veraltet) ────────────────────────────────
    ['toastr_SUCCESS', {
      tooltip: 'Zeigt eine grüne Erfolgsmeldung (Toast) mit Titel und Nachricht an.',
    }],
    ['toastr_ERROR', {
      tooltip: 'Zeigt eine rote Fehlermeldung (Toast) mit Titel und Nachricht an.',
    }],
    ['toastr_INFO', {
      tooltip: 'Zeigt eine blaue Infomeldung (Toast) mit Titel und Nachricht an.',
    }],
    ['toastr_WARNING', {
      tooltip: 'Zeigt eine gelbe Warnmeldung (Toast) mit Titel und Nachricht an.',
    }],
    ['toastr_options', {
      tooltip: 'Zusätzliche Einstellungen für eine Toast-Benachrichtigung. Die Dropdown-Felder „progressAnimation" (Fortschrittsbalken läuft ab- oder aufsteigend) und „positionClass" (Bildschirmposition der Meldung, z. B. oben rechts oder unten links) steuern das Aussehen zusätzlich zu den übrigen Optionen.',
    }],
    ['TOASTR_PRECONF', {
      tooltip: 'Zeigt eine vorkonfigurierte Toast-Benachrichtigung in einem wählbaren Stil mit Titel und Nachricht an. Optionen: „Success" (grün) für Erfolgsmeldungen, „Error" (rot) für Fehler, „Info" (blau) für Hinweise, „Warning" (gelb) für Warnungen.',
    }],

    // ── Webhook-Optionen (veraltet) ──────────────────────────────────────────
    ['webhook_options', {
      tooltip: 'Legt bis zu drei benutzerdefinierte Schlüssel-Wert-Paare als zusätzliche Webhook-Optionen fest.',
    }],
    ['webhook_custom_options', {
      tooltip: 'Fasst mehrere "Key: Value"-Einträge zu einem JSON-Objekt für zusätzliche Webhook-Optionen zusammen.',
    }],
    ['custom_option_item', {
      tooltip: 'Einzelner Schlüssel-Wert-Eintrag für den Baustein "Webhook-Optionen (JSON)".',
    }],

    // ── Referenz-Bausteine (Skill/Stage/Dokument per Name auswählen) ────────
    ['BLOCK_STACK_SKILL', {
      tooltip: 'Referenziert einen bestehenden Skill anhand seines Namens – zur Verwendung in anderen Bausteinen, die einen Skill als Eingabe erwarten. Wähle aus der Liste einen konkreten Skill aus, oder die Sonderoption „Alle Skills", um alle Skills gleichzeitig anzusprechen.',
    }],
    ['BLOCK_STACK_STAGE', {
      tooltip: 'Referenziert eine bestehende Stage anhand ihres Namens – zur Verwendung in anderen Bausteinen, die eine Stage als Eingabe erwarten. Neben den konkreten Stages stehen die Sonderoptionen „Erste Stage", „Letzte Stage", „Vorherige Stage" und „Nächste Stage" zur Auswahl, die sich relativ zur aktuellen Stage verhalten.',
    }],
    ['BLOCK_STACK_DOCUMENT', {
      tooltip: 'Referenziert ein bestehendes Dokument anhand seines Namens – zur Verwendung in anderen Bausteinen, die ein Dokument als Eingabe erwarten. Wähle aus der Liste eines der im Bot vorhandenen Dokumente aus.',
      categoryPath: 'Daten & Inhalte > Dokumente'
    }],
    ['BLOCK_STACK_DOCUMENT_ALL', {
      tooltip: 'Referenziert mehrere Dokumente auf einmal – zur Verwendung in der Dokumentenabfrage. „Alle Dokumente“ durchsucht alle Dokumente des Bots, „Alle aktiven Dokumente“ nur die, die für den aktuellen Nutzer laut Skill-Status aktiv sind (steuerbar über Skills aktivieren/deaktivieren). Bilder sind jeweils ausgenommen. Die Auswahl wird erst zur Laufzeit ermittelt, neue oder geänderte Dokumente werden also automatisch berücksichtigt.',
      categoryPath: 'Daten & Inhalte > Dokumente'
    }],
    // ── Interner Test-Baustein ───────────────────────────────────────────────
    ['aa', {
      tooltip: 'Interner Test-Baustein ohne Funktion, nicht für die reguläre Nutzung vorgesehen.',
    }],

    // ── Typumwandlungen ("als ... interpretieren") ───────────────────────────
    ['CAST_TO_EVENT', {
      tooltip: 'Wandelt einen Text in einen Ereignis-Bezeichner um, damit er an Bausteinen angeschlossen werden kann, die gezielt ein Ereignis erwarten.',
      categoryPath: 'Start & Ablauf > Ereignisse'
    }],
    ['CAST_TO_SKILL', {
      tooltip: 'Wandelt einen Text in eine Skill-Referenz um, damit er an Bausteinen angeschlossen werden kann, die gezielt einen Skill erwarten.',
      categoryPath: 'Start & Ablauf > Skills'
    }],
    ['CAST_TO_STAGE', {
      tooltip: 'Wandelt einen Text in eine Stage-Referenz um, damit er an Bausteinen angeschlossen werden kann, die gezielt eine Stage erwarten.',
      categoryPath: 'Start & Ablauf > Stages'
    }],

    // ── Quiz (weitere) ──────────────────────────────────────────────────────
    ['QUIZ_ACTIONS', {
      tooltip: 'Löst manuell eine Quiz-Aktion aus: die nächste Frage stellen oder das laufende Quiz vorzeitig beenden.',
      categoryPath: 'KI-Funktionen > Quiz'
    }]
  ]);

  public resolveStandardBlockHelp(
    blockId: string,
    blockTemplate: string,
    existingTooltip?: string,
    existingHelpUrl?: string
  ): ResolvedBlockHelp {
    const entry = this.helpEntries.get(blockId);
    const tooltip = this.composeTooltip(blockId, blockTemplate, existingTooltip, entry);
    const helpUrl = (existingHelpUrl && existingHelpUrl.trim() !== '')
      ? existingHelpUrl
      : this.buildStandardHelpUrl(blockId);

    return { tooltip, helpUrl };
  }

  private composeTooltip(
    blockId: string,
    blockTemplate: string,
    existingTooltip: string | undefined,
    entry: BlocklyHelpEntry | undefined
  ): string {
    const coreTooltip = (existingTooltip && existingTooltip.trim() !== '')
      ? existingTooltip.trim()
      : entry?.tooltip ?? this.buildFallbackTooltip(blockTemplate);

    const categoryHint = entry?.categoryPath ? `\nKategorie: ${entry.categoryPath}` : '';
    if (coreTooltip.includes(`🆔 ${blockId}`)) {
      return coreTooltip;
    }

    return `${coreTooltip}${categoryHint}\n\n🆔 ${blockId}`;
  }

  private buildStandardHelpUrl(blockId: string): string {
    const entry = this.helpEntries.get(blockId);
    return this.buildNewBlocklyDocsUrl(blockId, entry?.categoryPath) ?? this.getStandardDocsOverviewUrl();
  }

  // Slugify-Algorithmus muss 1:1 zu scripts/blockly-docs/generate.mjs passen, sonst matcht der Pfad nicht.
  private slugify(segment: string): string {
    return segment
      .toLowerCase()
      .replace(/&/g, 'und')
      .replace(/[^a-z0-9äöüß]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Baut die URL zur generierten Blockly-Dokumentation (scripts/blockly-docs). Anker ist immer die
   * blockId selbst (siehe {#blockId} in generate.mjs), unabhängig von der Block-Überschrift.
   * Primär wird der generierte Lookup (BLOCKLY_DOCS_MAP) genutzt, der ALLE extrahierten Blöcke abdeckt.
   * Der categoryPath-/EXPERT_-Präfix-basierte Fallback greift nur, falls ein Block dort (noch) fehlt,
   * z.B. direkt nach dem Anlegen eines neuen Blocks, bevor die CI-Doku-Generierung erneut lief.
   */
  public buildNewBlocklyDocsUrl(blockId: string, categoryPath?: string): string | null {
    const mappedPath = BLOCKLY_DOCS_MAP[blockId];
    if (mappedPath) {
      return `${this.docsBaseUrl}/${mappedPath}#${blockId}`;
    }

    if (blockId.startsWith('EXPERT_')) {
      const service = blockId.split('_')[1];
      return service ? `${this.docsBaseUrl}/Blockly-Expert/${this.slugify(service)}#${blockId}` : null;
    }
    if (!categoryPath) return null;
    const segments = categoryPath.split(' > ').filter(Boolean);
    const [topSegment, ...restSegments] = segments;
    if (!topSegment) return null;
    const path = restSegments.length > 0
      ? `${this.slugify(topSegment)}/${this.slugify(restSegments.join(' '))}`
      : this.slugify(categoryPath);
    return `${this.docsBaseUrl}/Blockly/${path}#${blockId}`;
  }

  // Ultimativer Fallback, falls ein Block weder in BLOCKLY_DOCS_MAP noch über categoryPath/EXPERT_-Präfix
  // auflösbar ist (z.B. ganz neuer Block vor dem nächsten CI-Doku-Lauf) - führt auf die Übersichtsseite
  // der generierten Doku statt auf die stillgelegte alte statische Hilfe-Seite.
  public getStandardDocsOverviewUrl(): string {
    return `${this.docsBaseUrl}/Blockly`;
  }

  public getExpertDocsOverviewUrl(): string {
    return `${this.docsBaseUrl}/Blockly-Expert`;
  }

  private buildFallbackTooltip(blockTemplate: string): string {
    const firstLine = this.extractFirstHumanLine(blockTemplate);
    if (!firstLine) {
      return 'Für diesen Block gibt es noch keine ausführliche Beschreibung. Ziehe ihn auf die Arbeitsfläche und passe die markierten Eingabefelder an deinen Anwendungsfall an.';
    }

    return `Für diesen Block gibt es noch keine ausführliche Beschreibung. Er führt aus: „${firstLine}". Passe die markierten Eingabefelder an deinen Anwendungsfall an.`;
  }

  private extractFirstHumanLine(blockTemplate: string): string {
    const line = blockTemplate
      .split('\n')
      .map((part) => part.trim())
      .find((part) => part.length > 0 && !part.startsWith('@')) ?? '';

    return line
      .replace(/{{[^}]*}}/g, '...')
      .replace(/[\u{1F300}-\u{1FAFF}]/gu, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
